import React, {
  useState, useEffect, useCallback, lazy, Suspense
} from 'react';
import PropTypes from 'prop-types';
import { toastr } from 'react-redux-toastr';
import { useSelector, useDispatch } from 'react-redux';
import { Switch, Route, Redirect } from 'react-router-dom';
import { useMediaPredicate } from 'react-media-hook';


import BackgroundDesktop from 'images/UI_CODE_ONE_Background_User_Horizontal.jpg';

import If from 'components/UI/If/If';
import LayoutNavTop from 'components/Layout/Nav/Top';
import LayoutNavLeft from 'components/Layout/Nav/Left';
import Footer from 'components/Layout/Footer/Footer';
import Messages from 'components/UI/Messages/Messages';
import Home from 'components/Home/Home';
import WaitingTickets from 'components/Tickets/WaitingTickets';
import MuiModal from 'components/UI/Modal/MuiModal';

import { Types as SidebarActions } from '../../store/sidebar';
import { Types as WaitingTicketsActions } from '../../store/waitingTickets';
import { Types as UserActions } from '../../store/user';
import { Types as TicketActions } from '../../store/ticket';

import { setAuthToken, removeAuthToken, setInvalidatedTokenCallback } from '../../utils/axiosAPI';
import { removeCookie } from '../../utils/cookie';

import ticketService from '../../services/ticketService';
import getErrorMessage from '../../utils/errorHandler';

import './ScreenApp.css';

const SelectWorkstation = lazy(() => import('../../components/Workstation/SelectWorkstation'));
const ScreenAttendance = lazy(() => import('../Attendance/ScreenAttendance'));
const ScreenSearchTicket = lazy(() => import('../SearchTicket/ScreenSearchTicket'));

function ScreenApp({ history }) {
  const [hideCallTicketButton, setHideCallTicketButton] = useState(false);
  const [disabledCallButton, setDisabledCallButton] = useState(false);
  const user = useSelector((state) => state.user.data);
  const sideBar = useSelector((state) => state.sideBar);
  const dispatch = useDispatch();
  const [showBackground, setShowBackground] = React.useState(false);
  const [moduleBg, setModuleBg] = React.useState();
  const [label, setLabel] = useState('');
  const defaultPriority = 1;
  const [showSearchedTicketsModal, setShowSearchedTicketsModal] = React.useState(false);
  const [searchedTickets, setSearchedTickets] = React.useState([]);
  const [searchedTicket, setSearchedTicket] = useState(undefined);

  const collapseSidebar = () => {
    dispatch({ type: SidebarActions.COLLAPSE_SIDEBAR });
  };

  const refreshWaitingTickets = useCallback(() => {
    if (!user.loggedOnWorkstation && user.office.timeTicketReload) { return; }

    dispatch({
      type: WaitingTicketsActions.REQUEST_WAITING_TICKETS
    });
  }, [user.loggedOnWorkstation, user.office.timeTicketReload]);

  const performLogout = () => {
    if (user.hasTicket) {
      toastr.error('', 'Para sair do sistema finalize seu atendimento');
      return;
    }

    dispatch({
      type: UserActions.REQUEST_LOGOUT,
      payload: {
        callback: () => {
          history.push('');
        }
      }
    });
  };

  function callNext() {
    setDisabledCallButton(true);

    dispatch({
      type: TicketActions.REQUEST_CALL_NEXT_TICKET,
      payload: {
        callback: () => {
          history.push('attendance');
        },
        onComplete: () => {
          setDisabledCallButton(false);
        }
      }
    });
  }

  useEffect(() => {
    refreshWaitingTickets();


    const interval = setInterval(() => {
      refreshWaitingTickets();
    }, user.office.timeTicketReload);

    return () => {
      clearInterval(interval);
    };
  }, [refreshWaitingTickets]);

  useEffect(() => {
    setAuthToken(user.token);

    setInvalidatedTokenCallback(() => {
      dispatch({ type: UserActions.TOKEN_INVALIDATED });
      dispatch({ type: TicketActions.UNATTACH_TICKET });

      removeCookie();
      removeAuthToken();

      history.push('/');
    });

    dispatch({
      type: UserActions.REQUEST_SELF_INFORMATIONS,
      payload: {
        redirect: () => {
          history.push('');
        }
      }
    });
  }, []);

  const isCallTicketButtonVisible = useCallback(() =>
    (user.loggedOnWorkstation && !user.hasTicket) && !hideCallTicketButton,
  [user.loggedOnWorkstation, user.hasTicket, hideCallTicketButton]);

  const desktopMediaQuery = useMediaPredicate('(min-width: 992px)');

  function handleSideBarImage() {
    if (!showBackground) {
      setModuleBg(null);
      return;
    }
    if (desktopMediaQuery) {
      setModuleBg(BackgroundDesktop);
    }
  }

  React.useEffect(() => {
    history.listen((location) => {
      setShowBackground(location.pathname.indexOf('/home') >= 0);
    });
  }, []);

  React.useEffect(() => {
    handleSideBarImage();
  }, [showBackground]);

  async function search(label) {
    if (!label || label.length === 0) {
      return;
    }

    try {
      const response = await ticketService.getTicketListByLabelAndStatus(encodeURIComponent(label) + '', '0');
      const tickets = response.data;

      if (Array.isArray(tickets) && tickets.length === 0) {
        toastr.warning('', 'Senha não encontrada');
        // removeFoundTicket();
        return;
      }

      if (tickets.length > 1) {
        // alert('Achou mais de uma senha!');

        setSearchedTickets(tickets);
        setLabel('');
        setShowSearchedTicketsModal(true);

        return
      }

      // alert('Achou apenas uma senha!');
      callTicket(tickets[0]);

      // setFoundList(response.data);

      setSearchedTickets([]);
      setLabel('');
      setShowSearchedTicketsModal(false);

    } catch (error) {
      // removeFoundTicket();
      toastr.error('', getErrorMessage(error));

      setSearchedTickets([]);
      setLabel('');
      setShowSearchedTicketsModal(false);
    }
  }

  function parseSearchedTicket(data) {
    return {
      id: data.id,
      label: data.label,
      status: data.status.code,
      statusDescription: data.status.description,
      emissionTimestamp: data.dateInsert,
      evolutionTimestamp: data.status.dateTime,
      description: data.description,
      calledTimes: data.calledTimes,
      line: data.line,
      customer: data.customer,
      ratingId: data.ratingId,
      ratingColor: data.ratingColor,
      ratingName: data.ratingName,
      ticketInformation: data.ticketInformation,
    };
  }

  function callTicket(ticket) {
    dispatch({
      type: TicketActions.FETCHED_TICKET,
      payload: parseSearchedTicket(ticket)
    });
    dispatch({
      type: TicketActions.REQUEST_CALL_TICKET,
      payload: {
        callback: () => {
          history.push('attendance');
          dispatch({ type: UserActions.HOLDING_TICKET, payload: true });
        },
        priority: defaultPriority,
        ticket: ticket
      }
    });
  }

  function selectSearchedTicket() {
    setShowSearchedTicketsModal(false);
    callTicket(searchedTicket);
  }

  return (
    <>
      <div className={showBackground ? 'wrapper customized-wrapper' : 'wrapper'} style={{ background: `url(${moduleBg})` }}>
        <LayoutNavLeft
          canSearchTicket={user.canSearchTicket}
          collapsed={sideBar.collapsed}
        />
        <div id="content">
          <LayoutNavTop
            callNext={callNext}
            collapse={collapseSidebar}
            disabledCallButton={disabledCallButton}
            environmentName={user.environmentName}
            label={label}
            loggedOnWorkstation={user.loggedOnWorkstation}
            login={user.login}
            logout={performLogout}
            search={search}
            setLabel={setLabel}
            showCallButton={isCallTicketButtonVisible()}
            userCallTicketDirectly={user.callTicketDirectly}
            userId={user.id}
            username={user.name}
            workstation={user.workstation}
          />
          <div id="main">
            <div className="row">
              <div className="col-12 col-sm-7 col-md-8">
                <If test={user.id !== 0}>
                  <Suspense fallback={<h1>Aguarde...</h1>}>
                    <Switch>
                      <Route
                        component={() => {
                          if (user.loggedOnWorkstation) {
                            setHideCallTicketButton(false);
                          }
                          return user && user.hasTicket
                            ? (
                              <Redirect to="/attendance" />
                            ) : (<Redirect to="/home" />);
                        }}
                        exact
                        path="/"
                      />

                      <Route
                        component={
                          () => {
                            setHideCallTicketButton(false);
                            return user.loggedOnWorkstation ? (<Redirect to="/home" />)
                              : (<SelectWorkstation userId={user.id} />);
                          }
                        }
                        path="/workstation"
                      />
                      <Route
                        component={(props) => {
                          setHideCallTicketButton(false);
                          return <ScreenAttendance {...props} user={user} />;
                        }}
                        path="/attendance"
                      />
                      <Route
                        component={() => {
                          setHideCallTicketButton(true);
                          return <ScreenSearchTicket history={history} />;
                        }}
                        path="/search"
                      />
                      <Route component={Home} path="/home" />
                    </Switch>
                  </Suspense>
                </If>
              </div>
              <div className="col-12 col-sm-5 col-md-4 pr-0">
                { user.loggedOnWorkstation
                  && <WaitingTickets workstationId={user.workstation.id} /> }
              </div>
            </div>
          </div>
          <Footer />
        </div>
      </div>
      <Messages />

      <MuiModal
        closeMethod={() => setShowSearchedTicketsModal(false)}
        onClickAway={() => setShowSearchedTicketsModal(false)}
        primaryButtonMethod={selectSearchedTicket}
        primaryButtonText="Chamar"
        secondaryButtonMethod={() => setShowSearchedTicketsModal(false)}
        secondaryButtonText="Cancelar"
        title="Senhas encontradas"
        visible={showSearchedTicketsModal}
      >
        <div className="row">
          <If test={searchedTickets && Array.isArray(searchedTickets) && searchedTickets.length > 0}>
            { searchedTickets.map((t) => (
              <div className="col-12" key={t.id}>
                <input
                  id={`found-ticket-${t.id}`}
                  name="found-ticket"
                  onClick={() => setSearchedTicket(t)}
                  type="radio"
                  value={t.id}
                />
                <label className="ml-1" htmlFor={`found-ticket-${t.id}`}>{`${t.line.name} - ${t.label} ${t.dateInsert} - ${t.status.description}`}</label>
              </div>
            ))}
          </If>
        </div>
      </MuiModal>
    </>
  );
}

ScreenApp.propTypes = {
  history: PropTypes.shape({
    push: PropTypes.func.isRequired
  }).isRequired
};

export default ScreenApp;
